// CustomerListTableWithDialog.jsx
import { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  Link,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { post } from 'api';
import moment from 'moment';
import { enqueueSnackbar } from 'notistack';
import IconifyIcon from 'components/base/IconifyIcon';
import DataGridPagination from 'components/pagination/DataGridPagination';
import StyledTextField from 'components/styled/StyledTextField';

const InstallationCheckList = () => {
  const columns = useMemo(
    () => [
      // {
      //   field: 'view',
      //   headerName: 'View',
      //   sortable: false,
      //   minWidth: 80,
      //   renderCell: () => (
      //     <Link variant="body2" sx={{ fontWeight: 400 }} href="/customer-details">
      //       <RemoveRedEyeOutlinedIcon />
      //     </Link>
      //   ),
      // },
      {
        field: 'client',
        headerName: 'Client',
        minWidth: 340,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const { firstName, lastName, mobile } = params.row;
          return (
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Avatar
                alt={firstName.toUpperCase()}
                src={firstName}
                sx={{ width: 24, height: 24, bgcolor: '#977efdff' }}
              />
              <Box>
                <Typography variant="subtitle2">{`${firstName} ${lastName}`}</Typography>
                <Link href="#!" variant="caption">
                  {mobile || ''}
                </Link>
              </Box>
            </Stack>
          );
        },
      },
      {
        field: 'meterFilePrepared',
        headerName: 'Meter File Prepared',
        minWidth: 150,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Stack>
            <Chip
              label={params?.row?.installationmanagements.meterFile ? 'yes' : 'no'}
              variant="soft"
              color={getStatusBadgeColor(
                params?.row?.installationmanagements.meterFile ? 'yes' : 'no',
              )}
              sx={{ textTransform: 'capitalize' }}
            />
          </Stack>
        ),
      },
      {
        field: 'installationStart',
        headerName: 'Installation Start',
        minWidth: 150,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const { installationmanagements } = params.row;
          return (
            <Stack>
              <Chip
                label={
                  installationmanagements?.installationStart
                    ? `${moment(installationmanagements?.installationStartDate).format('DD/MM/YYYY')}`
                    : 'no'
                }
                variant="soft"
                color={getStatusBadgeColor(
                  installationmanagements.installationStart ? 'yes' : 'no',
                )}
                sx={{ textTransform: 'capitalize' }}
              />
            </Stack>
          );
        },
      },
      {
        field: 'installationDone',
        headerName: 'Installation Done',
        minWidth: 150,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const { installationmanagements } = params.row;
          return (
            <Stack>
              <Chip
                label={
                  installationmanagements?.installationDone
                    ? `${moment(installationmanagements?.installationDoneDate).format('DD/MM/YYYY')}`
                    : 'no'
                }
                variant="soft"
                color={getStatusBadgeColor(installationmanagements.installationDone ? 'yes' : 'no')}
                sx={{ textTransform: 'capitalize' }}
              />
            </Stack>
          );
        },
      },
      {
        field: 'isMeterInstalled',
        headerName: 'Meter Installed',
        minWidth: 150,
        sortable: false,
        filterable: false,
        flex: 1,
        renderCell: (params) => (
          <Stack>
            <Chip
              label={params?.row?.installationmanagements?.meterInstalled ? 'yes' : 'no'}
              variant="soft"
              color={getStatusBadgeColor(
                params?.row?.installationmanagements?.meterInstalled ? 'yes' : 'no',
              )}
              sx={{ textTransform: 'capitalize' }}
            />
          </Stack>
        ),
      },
      {
        field: 'installation',
        headerName: 'Installation CheckList',
        width: 200,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Button variant="outlined" size="small" onClick={() => handleOpenDialog(params.row)}>
            Update
          </Button>
        ),
      },
      // {
      //   field: 'action',
      //   headerName: 'Action',
      //   sortable: false,
      //   align: 'right',
      //   width: 60,
      //   headerAlign: 'right',
      //   renderCell: () => <DashboardMenu />,
      // },
    ],
    [],
  );
  const [data, setData] = useState([]);
  const [search, setSearch] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [formData, setFormData] = useState({
    meterFile: '',
    installationStart: '',
    installationStartDate: null,
    installationDone: '',
    installationDoneDate: null,
    meterInstalled: '',
  });
  const [total, setTotal] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
    search: '',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const timeOut = setTimeout(() => {
      setPaginationModel((prev) => ({ ...prev, search }));
    }, 700);
    return () => clearTimeout(timeOut);
  }, [search]);
  useEffect(() => {
    if (selectedRow) {
      setFormData({
        meterFile: selectedRow.installationmanagements?.meterFile || false,
        installationStart: selectedRow.installationmanagements?.installationStart || false,
        installationStartDate: selectedRow.installationmanagements?.installationStartDate || null,
        installationDone: selectedRow.installationmanagements?.installationDone || false,
        installationDoneDate: selectedRow.installationmanagements?.installationDoneDate || null,
        meterInstalled: selectedRow.installationmanagements?.meterInstalled || false,
      });
    }
  }, [selectedRow]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updated = { ...prev };
      if (name == 'installationStartDate' || name == 'installationDoneDate') {
        updated[name] = value;
      } else {
        updated[name] = value === 'true';
      }
      // reset dependent fields if step breaks
      if (name === 'meterFile' && value !== 'true') {
        updated.installationStart = false;
        updated.installationStartDate = null;
        updated.installationDone = false;
        updated.installationDoneDate = null;
        updated.meterInstalled = false;
      }
      if (name === 'installationStart' && value !== 'true') {
        updated.installationDone = false;
        updated.installationDoneDate = null;
        updated.meterInstalled = false;
      }
      if (name === 'installationDone' && value !== 'true') {
        updated.meterInstalled = false;
      }
      return updated;
    });
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      // /list_installationManagement
      const response = await post('users/list_customers_stages', {
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
        search: paginationModel.search,
        type: 'installation',
      });
      if (response.status == 200) {
        setData(response.data.data);
        setTotal(response.data.total);
      } else {
        enqueueSnackbar(response.message || 'Error while Fetching Customer', {
          variant: 'error',
          autoHideDuration: 3000,
        });
        setData([]);
      }
    } catch (error) {
      enqueueSnackbar(error.message || 'Error while Fetching Customer', {
        variant: 'error',
        autoHideDuration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchData();
  }, [paginationModel]);

  const handleOpenDialog = (row) => {
    setSelectedRow(row);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedRow(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (formData.installationStart && formData.installationStartDate == null) {
        formData.installationStartDate = moment().format('YYYY-MM-DD');
      }
      if (formData.installationDone && formData.installationDoneDate == null) {
        formData.installationDoneDate = moment().format('YYYY-MM-DD');
      }
      const response = await post(
        `users/update_installationManagement/${selectedRow.installationmanagements._id}`,
        formData,
      );

      if (response.status === 200) {
        enqueueSnackbar('GEDA details updated successfully', { variant: 'success' });
        handleCloseDialog();
        fetchData(); // refresh table
      } else {
        enqueueSnackbar(response.message || 'Failed to update GEDA details', { variant: 'error' });
      }
    } catch (error) {
      enqueueSnackbar(error.message || 'Something went wrong', { variant: 'error' });
    }
  };

  const getStatusBadgeColor = (val) => {
    switch (val) {
      case 'yes':
        return 'success';
      case 'hold':
        return 'info';
      case 'reject':
        return 'error';
      case 'no':
        return 'error';
      default:
        return 'info';
    }
  };

  return (
    <>
      <Box sx={{ width: '100%' }}>
        <StyledTextField
          id="search-box"
          type="search"
          fullWidth
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search customer name"
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <IconifyIcon icon="material-symbols:search-rounded" fontSize={20} />
                </InputAdornment>
              ),
            },
          }}
          sx={{
            maxWidth: { sm: 200, md: 240 },
            flexGrow: { xs: 1, sm: 0 },
            mb: 3,
          }}
        />
        <DataGrid
          loading={loading}
          rowHeight={72}
          rows={data}
          columns={columns}
          getRowId={(row) => row._id}
          pagination
          paginationMode="server"
          rowCount={total}
          paginationModel={paginationModel} // { page: 0, pageSize: 10 }
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[5, 10, 20]}
          sx={{
            '& .margin': {
              pr: 3,
            },
            '& .MuiDataGrid-columnHeaders': {
              minWidth: '100%',
            },
          }}
          slots={{
            basePagination: (props) => <DataGridPagination showFullPagination {...props} />,
          }}
        />
      </Box>
      {/* DIALOG FORM */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Update Installation Details – {`${selectedRow?.firstName} ${selectedRow?.lastName}`}
        </DialogTitle>

        <DialogContent>
          <Box component="form" id="gedaForm" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            {/* a. Document uploaded */}
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              a. Is meter file prepared
            </Typography>
            <RadioGroup row name="meterFile" value={formData.meterFile} onChange={handleChange}>
              <FormControlLabel value={true} control={<Radio />} label="Yes" />
              <FormControlLabel value={false} control={<Radio />} label="No" />
            </RadioGroup>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              b. Installation Start
            </Typography>
            <RadioGroup
              row
              name="installationStart"
              value={formData.installationStart}
              onChange={handleChange}
            >
              <FormControlLabel
                value={true}
                control={<Radio />}
                disabled={formData.meterFile !== true}
                label="Yes"
              />
              <FormControlLabel
                value={false}
                control={<Radio />}
                disabled={formData.meterFile !== true}
                label="No"
              />
            </RadioGroup>
            <TextField
              name="installationStartDate"
              label="Installation Start Date"
              type="date"
              variant="filled"
              disabled={formData.installationStart !== true}
              onChange={handleChange}
              defaultValue={moment().format('YYYY-MM-DD')}
              fullWidth
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
                htmlInput: {
                  max: moment().format('YYYY-MM-DD'), // 👉 disables future dates in calendar
                  // ❗ blocks browser tooltip
                },
              }}
            />
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              c. Installation Done
            </Typography>
            <RadioGroup
              row
              name="installationDone"
              value={formData.installationDone}
              onChange={handleChange}
            >
              <FormControlLabel
                value={true}
                control={<Radio />}
                disabled={formData.installationStartDate == null}
                label="Yes"
              />
              <FormControlLabel
                value={false}
                control={<Radio />}
                disabled={formData.installationStartDate == null}
                label="No"
              />
            </RadioGroup>
            <TextField
              name="installationDoneDate"
              label="Installation Done Date"
              type="date"
              variant="filled"
              disabled={formData.installationDone !== true}
              onChange={handleChange}
              defaultValue={moment().format('YYYY-MM-DD')}
              fullWidth
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
                htmlInput: {
                  max: moment().format('YYYY-MM-DD'), // 👉 disables future dates in calendar
                  // ❗ blocks browser tooltip
                },
              }}
            />

            {/* b. Approved by GEDA */}
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>
              d. Is meter installed at site
            </Typography>
            <RadioGroup
              row
              name="meterInstalled"
              value={formData.meterInstalled}
              onChange={handleChange}
            >
              <FormControlLabel
                value={true}
                control={<Radio />}
                label="Yes"
                disabled={formData.installationDoneDate == null}
              />
              <FormControlLabel
                value={false}
                control={<Radio />}
                label="No"
                disabled={formData.installationDoneDate == null}
              />
            </RadioGroup>

            {/* d. Meter charges */}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button type="submit" form="gedaForm" variant="contained">
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default InstallationCheckList;
